import confirmTransferDetailsValidation from "../utils/confirmTransferDetailsValidation";
import { convertToAPIDate } from "../utils/formateDate";
import formatPhoneNumber from "../utils/formatPhoneNumber";
import mapKnownValues from "../utils/mapKnownValues";
import { MartialStatusResponse, TransferIntentResponse } from "./transferIntent";

export const MARRIED = "Married";
export const NOT_MARRIED = "Not Married";

const stateMap = new Map();
stateMap.set("AL", "Alabama");
stateMap.set("AK", "Alaska");
stateMap.set("AZ", "Arizona");
stateMap.set("AR", "Arkansas");
stateMap.set("AS", "American Samoa");
stateMap.set("CA", "California");
stateMap.set("CO", "Colorado");
stateMap.set("CT", "Connecticut");
stateMap.set("DE", "Delaware");
stateMap.set("DC", "District of Columbia");
stateMap.set("FL", "Florida");
stateMap.set("GA", "Georgia");
stateMap.set("HI", "Hawaii");
stateMap.set("ID", "Idaho");
stateMap.set("IL", "Illinois");
stateMap.set("IN", "Indiana");
stateMap.set("IA", "Iowa");
stateMap.set("KS", "Kansas");
stateMap.set("KY", "Kentucky");
stateMap.set("LA", "Louisiana");
stateMap.set("ME", "Maine");
stateMap.set("MD", "Maryland");
stateMap.set("MA", "Massachusetts");
stateMap.set("MI", "Michigan");
stateMap.set("MN", "Minnesota");
stateMap.set("MO", "Missouri");
stateMap.set("MS", "Mississippi");
stateMap.set("MT", "Montana");
stateMap.set("NE", "Nebraska");
stateMap.set("NV", "Nevada");
stateMap.set("NH", "New Hampshire");
stateMap.set("NJ", "New Jersey");
stateMap.set("NM", "New Mexico");
stateMap.set("NY", "New York");
stateMap.set("NC", "North Carolina");
stateMap.set("ND", "North Dakota");
stateMap.set("OH", "Ohio");
stateMap.set("OK", "Oklahoma");
stateMap.set("OR", "Oregon");
stateMap.set("PA", "Pennsylvania");
stateMap.set("RI", "Rhode Island");
stateMap.set("SC", "South Carolina");
stateMap.set("SD", "South Dakota");
stateMap.set("TN", "Tennessee");
stateMap.set("TX", "Texas");
stateMap.set("UT", "Utah");
stateMap.set("VT", "Vermont");
stateMap.set("VA", "Virginia");
stateMap.set("VI", "Virgin Islands");
stateMap.set("WA", "Washington");
stateMap.set("WV", "West Virginia");
stateMap.set("WI", "Wisconsin");
stateMap.set("WY", "Wyoming");

export const convertFullStateFromCode = (stateCode: string): string => {
  return stateMap.get(stateCode.toUpperCase()) || "";
};

const convertCitizenship = (response: string | undefined): UserDetails["citizenship"] => {
  if (response === "US" || response === "United States") {
    return "United States";
  }
  return "";
};

export type UserDetailsResponse = {
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  citizenship: string;
  dateOfBirth: string;
  ssnOrTin: string;
  maritalStatus: string;
  spouseEmail: string;
  spouseName: string;
  spousePhoneNumber: string;
  spouseDateOfBirth: string;
  signatureFileName: string;
  userOnboarded: boolean;
  userIdentityId: number;
  address: {
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
  } | null;
};

export class Address {
  addressLine1 = "";
  addressLine2 = "";
  city = "";
  state = "";
  zipCode = "";
  country = "";
  stateFullName = "";

  constructor(params: Partial<Address> = {}) {
    mapKnownValues(this, params);
    if (params.state && params.state.length === 2) {
      this.state = convertFullStateFromCode(params.state);
    }
  }
}

class UserDetails {
  firstName = "";
  middleName = "";
  lastName = "";
  suffix = "";
  dateOfBirth: string | null = null;
  citizenship = "";
  ssnOrTin: string | null = null;
  maritalStatus = "";
  spouseName = "";
  spouseEmail = "";
  spousePhoneNumber = "";
  signatureFileName = "";
  userIdentityId = -1;
  address = new Address();
  spouseDateOfBirth: string | null = null;
  userOnboarded = false;
  client: any;

  constructor(params: Partial<UserDetails> = {}) {
    mapKnownValues(this, params);
    if (!params.address) {
      this.address = new Address();
    } else {
      this.address = new Address(this.address);
    }
  }

  static fromTransferIntent(response: TransferIntentResponse["user"]) {
    if (!response) return new UserDetails();
    return new UserDetails({
      ...response,
      address: response.address ? new Address(response.address) : new Address(),
      citizenship: convertCitizenship(response.citizenship),
      maritalStatus:
        response.married === MartialStatusResponse.YES
          ? MARRIED
          : response.married === MartialStatusResponse.NO
            ? NOT_MARRIED
            : undefined,
    });
  }
  static toAPI(data: Partial<UserDetails>) {
    return new UserDetails({
      ...data,
      address: data.address ? new Address(data.address) : new Address(),
      dateOfBirth: data.dateOfBirth ? convertToAPIDate(data.dateOfBirth) : null,
      spouseDateOfBirth: data.spouseDateOfBirth ? convertToAPIDate(data.spouseDateOfBirth) : null,
      spousePhoneNumber: data.spousePhoneNumber ? `+1${formatPhoneNumber(data.spousePhoneNumber)}` : undefined,
    });
  }
  static fromAPI(
    data:
      | {
          userDetails: UserDetailsResponse;
          signatureUrl?: string;
        }
      | undefined,
  ) {
    if (!data) return new UserDetails();
    return new UserDetails({
      ...data.userDetails,
      signatureFileName: data.signatureUrl,
      address: data.userDetails?.address ? new Address(data.userDetails.address) : new Address(),
    });
  }
  static combineIntentAndAPI({
    intentResponse,
    apiResponse,
  }: {
    intentResponse: TransferIntentResponse["user"];
    apiResponse: UserDetailsResponse;
  }) {
    const nonNullIntent = Object.entries(UserDetails.fromTransferIntent(intentResponse)).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        //  @ts-ignore
        acc[key] = value;
      }
      return acc;
    }, {});

    return new UserDetails({
      ...this.fromAPI({ userDetails: apiResponse }),
      ...nonNullIntent,
    });
  }
  get isCompleted() {
    let isCompleted = false;

    if (
      confirmTransferDetailsValidation.validatePersonalDetails(this).allPassed &&
      confirmTransferDetailsValidation.validateMailingAddress(this).allPassed &&
      (this.maritalStatus === "Married" ? confirmTransferDetailsValidation.validateSpouseInfo(this).allPassed : true) &&
      this.signatureFileName
    ) {
      isCompleted = true;
    }
    return isCompleted;
  }
}

export default UserDetails;
